// src/components/audition-booking-wizard/Step2_Payment.jsx

import React from "react";
import { Button, Form, Row, Col, Spinner, Table, Badge, Alert } from "react-bootstrap";

const Step2_Payment = ({
  audition,
  isAuthenticated,
  user,
  newRegisterUser,
  apiError,
  authError,
  nameInput,
  setNameInput,
  emailInput,
  setEmailInput,
  mobileInput,
  setMobileInput,
  otpInput,
  setOtpInput,
  showInlineOtpSection,
  isSendingAuthOtp,
  isVerifyingAuthOtp,
  handleResendOtp,
  handleVerifyInlineOtp,
  timer,
  resendDisabled,
  loading,
  handleNext
}) => {

  // ✅ Regex Validators
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (value.length <= 50) setNameInput(value);
  };

  const handleEmailChange = (e) => {
    setEmailInput(e.target.value);
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) {
      setMobileInput(value);
    }
  };

  const isNameValid = nameInput.length >= 3 && nameInput.length <= 50;
  const isEmailValid = emailRegex.test(emailInput);
  const isMobileValid = mobileInput.length === 10;

  return (
    <div className="payment-step-container p-4 animate-fade-in">

      {apiError && <Alert variant="danger">{apiError}</Alert>}
      {authError && <Alert variant="danger">{authError}</Alert>}

      <Row className="g-4">

        {/* ================= USER DETAILS ================= */}

        <Col md={4} className="border-end border-light border-opacity-10">

          <h6 className="section-step-title">1. YOUR DETAILS:</h6>

          {isAuthenticated ? (
            <div className="user-info-box p-3 rounded mb-4">
              <h6 className="text-dark fw-bold mb-1">{user?.full_name}</h6>
              <p className="small mb-0">{user?.email}</p>
              <Badge bg="success" className="mt-2">
                Synced
              </Badge>
            </div>
          ) : (
            <>

              {/* NAME */}

              <Form.Group className="mb-3">
                <Form.Control
                  className="custom-input"
                  placeholder="Enter Name"
                  value={nameInput}
                  minLength={3}
                  maxLength={50}
                  onChange={handleNameChange}
                  disabled={showInlineOtpSection || isSendingAuthOtp || isVerifyingAuthOtp}
                  isInvalid={nameInput && !isNameValid}
                />
                <Form.Control.Feedback type="invalid">
                  Name must be between 3 and 50 characters
                </Form.Control.Feedback>
              </Form.Group>

              {/* EMAIL */}

              <Form.Group className="mb-3">
                <Form.Control
                  type="email"
                  className="custom-input"
                  placeholder="Enter Email"
                  value={emailInput}
                  maxLength={100}
                  onChange={handleEmailChange}
                  disabled={showInlineOtpSection || isSendingAuthOtp || isVerifyingAuthOtp}
                  isInvalid={emailInput && !isEmailValid}
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a valid email address
                </Form.Control.Feedback>
              </Form.Group>

              {/* MOBILE */}

              <Form.Group className="mb-4">
                <Form.Control
                  type="tel"
                  className="custom-input"
                  placeholder="Enter Mobile"
                  value={mobileInput}
                  minLength={10}
                  maxLength={10}
                  pattern="[0-9]{10}"
                  onChange={handleMobileChange}
                  disabled={showInlineOtpSection || isSendingAuthOtp || isVerifyingAuthOtp}
                  isInvalid={mobileInput && !isMobileValid}
                />
                <Form.Control.Feedback type="invalid">
                  Mobile number must be exactly 10 digits
                </Form.Control.Feedback>
              </Form.Group>

              {/* OTP SECTION */}

              {showInlineOtpSection && (
                <div className="mt-4 p-3 bg-dark rounded">

                  <p className="small text-light mb-2">
                    OTP sent to {emailInput || mobileInput}
                  </p>

                  <Form.Group className="mb-2">
                    <Form.Control
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      className="custom-input"
                      maxLength={6}
                      value={otpInput}
                      onChange={(e) =>
                        setOtpInput(e.target.value.replace(/\D/g, ""))
                      }
                      disabled={isVerifyingAuthOtp}
                    />
                  </Form.Group>

                  <Button
                    variant="link"
                    onClick={handleResendOtp}
                    className="btn-white small p-0 mb-2"
                    disabled={resendDisabled || isSendingAuthOtp}
                  >
                    {isSendingAuthOtp ? (
                      <Spinner animation="border" size="sm" />
                    ) : resendDisabled ? (
                      `Resend OTP in ${timer}s`
                    ) : (
                      "Resend OTP"
                    )}
                  </Button>

                  <Button
                    className="btn-danger w-100"
                    onClick={handleVerifyInlineOtp}
                    disabled={isVerifyingAuthOtp || otpInput.length !== 6}
                  >
                    {isVerifyingAuthOtp ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      "Verify OTP & Continue"
                    )}
                  </Button>

                </div>
              )}

            </>
          )}

          {/* COUPON */}

          <p className="small fw-bold mb-2">Coupon Code:</p>

          <div className="d-flex gap-2">
            <Form.Control className="custom-input" placeholder="CODE" />
            <Button className="btn-coupon-apply">Apply</Button>
          </div>

        </Col>

        {/* ================= SUMMARY ================= */}

        <Col md={4} className="text-dark px-4">

          <h6 className="section-step-title">SUMMARY</h6>

          <Table borderless size="sm" className="summary-table">
            <tbody>

              <tr>
                <td>Role</td>
                <td className="text-end fw-bold">
                  {audition?.roleName}
                </td>
              </tr>

              <tr>
                <td>Mode</td>
                <td className="text-end fw-bold">
                  {audition?.displayAuditionTypes?.join(", ")}
                </td>
              </tr>

              <tr>
                <td>Amount</td>
                <td className="text-end fw-bold">
                  ₹{audition?.price}/-
                </td>
              </tr>

              <tr className="border-top">
                <td><strong>GST (9%)</strong></td>
                <td className="text-end fw-bold">
                  ₹{audition?.gst}/-
                </td>
              </tr>

              <tr className="border-top">
                <td><strong>Total</strong></td>
                <td className="text-end fw-bold">
                  ₹{audition?.totalAmount}/-
                </td>
              </tr>

            </tbody>
          </Table>

        </Col>

        {/* ================= PAYMENT ================= */}

        <Col md={4} className="border-start border-light border-opacity-10 ps-4">

          <h6 className="section-step-title">2. PAYMENT :</h6>

          {loading || isSendingAuthOtp || isVerifyingAuthOtp ? (

            <Button className="pay-btn disabled">
              <Spinner animation="border" size="sm" />
              {" "}SYNCING...
            </Button>

          ) : (

            <Button
              className="pay-btn mb-3"
              onClick={handleNext}
              disabled={
                !isAuthenticated &&
                (!isNameValid || !isEmailValid || !isMobileValid)
              }
            >
              {isAuthenticated
                ? `PAY ONLINE - ₹${audition?.totalAmount}/-`
                : showInlineOtpSection
                ? "Waiting for OTP Verification..."
                : "CONTINUE TO PAYMENT"}
            </Button>

          )}

        </Col>

      </Row>
    </div>
  );
};

export default Step2_Payment;